import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
  Legend,
  Tooltip
} from 'recharts';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import toast from 'react-hot-toast';

// Helper function to calculate dynamic X-axis domain and ticks
const getDynamicXAxisConfig = (data) => {
  if (!data || data.length === 0) return { domain: [0, 150], ticks: [0, 30, 60, 90, 120, 150] };

  // Get all values from the data
  const allValues = data.flatMap((item) => [item.planned || 0, item.actual || 0, item.total || 0]);
  const maxValue = Math.max(...allValues);

  // If any value exceeds 150, cap at 150, otherwise use natural range
  if (maxValue > 150) {
    return { domain: [0, 150], ticks: [0, 30, 60, 90, 120, 150] };
  } else if (maxValue === 0) {
    // If all values are 0, show a small range with 3 ticks
    return { domain: [0, 20], ticks: [0, 10, 20] };
  } else {
    // Calculate appropriate domain and ticks based on actual max value
    let finalMax = maxValue;
    let tickInterval;

    // Determine appropriate tick interval based on max value
    if (maxValue <= 5) {
      finalMax = 5;
      tickInterval = 2;
    } else if (maxValue <= 10) {
      finalMax = 10;
      tickInterval = 5;
    } else if (maxValue <= 20) {
      finalMax = Math.ceil(maxValue / 5) * 5; // Round up to nearest 5
      tickInterval = Math.ceil(finalMax / 4); // 5 ticks, rounded up
    } else if (maxValue <= 50) {
      finalMax = Math.ceil(maxValue / 10) * 10; // Round up to nearest 10
      tickInterval = Math.ceil(finalMax / 5); // 6 ticks, rounded up
    } else {
      finalMax = Math.ceil(maxValue / 10) * 10; // Round up to nearest 10
      tickInterval = Math.ceil(finalMax / 5); // 6 ticks, rounded up
    }

    const tickCount = Math.ceil(finalMax / tickInterval);
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => i * tickInterval);
    return { domain: [0, finalMax], ticks };
  }
};

// Helper function to calculate dynamic Y-axis domain and ticks for money charts
const getDynamicYAxisConfig = (data) => {
  if (!data || data.length === 0) return { domain: [0, 1000], ticks: [0, 250, 500, 750, 1000] };

  // Get all values from the data
  const allValues = data.map((item) => item.total || 0);
  const maxValue = Math.max(...allValues);

  if (maxValue === 0) {
    // If all values are 0, show a small range with 3 ticks
    return { domain: [0, 1000], ticks: [0, 250, 500, 750, 1000] };
  } else {
    // Calculate appropriate domain and ticks based on actual max value
    let finalMax = maxValue;
    let tickInterval;

    // Determine appropriate domain and ticks based on max value
    if (maxValue <= 500) {
      finalMax = Math.max(1000, maxValue);
      return { domain: [0, finalMax], ticks: [0, 250, 500, 750, 1000] };
    } else if (maxValue <= 1000) {
      finalMax = Math.max(1000, maxValue);
      return { domain: [0, finalMax], ticks: [0, 250, 500, 750, 1000] };
    } else if (maxValue <= 2000) {
      finalMax = Math.max(2000, maxValue);
      return { domain: [0, finalMax], ticks: [0, 500, 1000, 1500, 2000] };
    } else if (maxValue <= 5000) {
      finalMax = Math.max(5000, maxValue);
      return { domain: [0, finalMax], ticks: [0, 1000, 2000, 3000, 4000, 5000] };
    } else if (maxValue <= 10000) {
      finalMax = Math.max(10000, maxValue);
      return { domain: [0, finalMax], ticks: [0, 2000, 4000, 6000, 8000, 10000] };
    } else if (maxValue <= 15000) {
      finalMax = Math.max(15000, maxValue);
      return { domain: [0, finalMax], ticks: [0, 3000, 6000, 9000, 12000, 15000] };
    } else {
      // For values > 15000, calculate appropriate ticks without capping
      const roundedMax = Math.ceil(maxValue / 1000) * 1000; // Round up to nearest 1000
      const tickCount = Math.min(6, Math.ceil(roundedMax / 1000)); // Max 6 ticks
      const tickInterval = Math.ceil(roundedMax / tickCount / 1000) * 1000;
      const ticks = Array.from({ length: tickCount + 1 }, (_, i) => i * tickInterval);
      return { domain: [0, roundedMax], ticks };
    }
  }
};

// Custom label function for planned duration
const PlannedDurationLabel = ({ x, y, width, height, value, payload }) => {
  if (!value && value !== 0) return null; // hide if undefined/null, but show 0

  // Use original value if available, otherwise use display value
  const displayValue = payload?.plannedOriginal || value;

  if (displayValue === 0) {
    // For zero planned duration values, show on the right side
    return (
      <text
        x={x + width + 5}
        y={y + 15}
        fill="#6B7280"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize="10"
        fontWeight="bold">
        {displayValue}
      </text>
    );
  } else {
    // Check if the bar extends beyond the chart area
    const chartMaxX = 150; // This corresponds to our XAxis domain max when overflow occurs
    const barEndX = x + width;
    const exceedsMax = barEndX >= chartMaxX;

    if (exceedsMax) {
      // Bar extends beyond chart area, show label at the chart border
      return (
        <text
          x={chartMaxX - 5}
          y={y + height / 2}
          fill="white"
          textAnchor="end"
          dominantBaseline="middle"
          fontSize="10"
          fontWeight="bold">
          {displayValue}
        </text>
      );
    } else {
      // Bar is within chart area, show label inside the bar
      return (
        <text
          x={x + width - 5}
          y={y + height / 2}
          fill="white"
          textAnchor="end"
          dominantBaseline="middle"
          fontSize="10"
          fontWeight="bold">
          {displayValue}
        </text>
      );
    }
  }
};

// Custom label function for actual duration
const ActualDurationLabel = ({ x, y, width, height, value, payload }) => {
  if (!value && value !== 0) return null; // hide if undefined/null, but show 0

  // Use original value if available, otherwise use display value
  const displayValue = payload?.actualOriginal || value;

  if (displayValue === 0) {
    // For zero actual duration values, show "Yet to start" on the right side
    return (
      <text
        x={x + width + 5}
        y={y + 15}
        fill="#0EA5E9"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize="10"
        fontWeight="bold">
        Yet to start
      </text>
    );
  } else {
    // Check if the bar extends beyond the chart area
    const chartMaxX = 150; // This corresponds to our XAxis domain max when overflow occurs
    const barEndX = x + width;
    const exceedsMax = barEndX >= chartMaxX;

    if (exceedsMax) {
      // Bar extends beyond chart area, show label at the chart border
      return (
        <text
          x={chartMaxX - 5}
          y={y + height / 2}
          fill="white"
          textAnchor="end"
          dominantBaseline="middle"
          fontSize="10"
          fontWeight="bold">
          {displayValue}
        </text>
      );
    } else {
      // Bar is within chart area, show label inside the bar
      return (
        <text
          x={x + width - 5}
          y={y + height / 2}
          fill="white"
          textAnchor="end"
          dominantBaseline="middle"
          fontSize="10"
          fontWeight="bold">
          {displayValue}
        </text>
      );
    }
  }
};

const ProgressChart = ({
  title,
  chartType,
  rawData,
  isSubmitting,
  control,
  options,
  selectedValues,
  onSelectionChange,
  xAxisLabel = 'Days',
  showLegend = true
}) => {
  // Local state for dropdown values (doesn't trigger API calls)
  const [localSelectedValues, setLocalSelectedValues] = useState(selectedValues);

  // Update local state when selectedValues prop changes (for initial load)
  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  const isProgressChart = chartType === 'progress';
  const isMoneyChart = chartType === 'money';

  // Transform raw data based on chart type
  const data = useMemo(() => {
    if (!rawData) return [];

    if (isProgressChart) {
      return Object.entries(rawData).map(([key, value]) => {
        // Check for message key and show toast warning if present
        if (value.message) {
          toast(value.message, {
            icon: '⚠️',
            style: {
              background: '#fef3c7',
              color: '#d97706',
              border: '1px solid #f59e0b'
            }
          });
        }

        return {
          name: key,
          planned: value.planned_duration || 0,
          actual: value.actual_duration || 0,
          plannedOriginal: value.planned_duration || 0,
          actualOriginal: value.actual_duration || 0,
          status: value.status || 'Unknown'
        };
      });
    } else {
      return Object.entries(rawData).map(([key, value]) => {
        // Check for message key and show toast warning if present
        if (value.message) {
          toast(value.message, {
            icon: '⚠️',
            style: {
              background: '#fef3c7',
              color: '#d97706'
              // border: '1px solid #f59e0b'
            }
          });
        }

        return {
          name: key,
          total: value.total || 0
        };
      });
    }
  }, [rawData, isProgressChart]);

  // Calculate dynamic bottom margin based on x-axis label length
  const calculateBottomMargin = () => {
    if (!data || data.length === 0) return 30;

    // Get the longest label name
    const longestLabel = data.reduce((longest, item) => {
      const currentLength = item.name ? item.name.length : 0;
      return currentLength > longest ? currentLength : longest;
    }, 0);

    // Base margin is 30px, add extra margin for longer labels
    if (longestLabel <= 15) return 30;
    if (longestLabel <= 25) return 50;
    if (longestLabel <= 35) return 70;
    if (longestLabel <= 45) return 90;
    return 110; // For very long labels
  };

  return (
    <div className="p-4 w-full max-w-4xl mx-auto bg-white rounded-xl flex flex-col justify-start items-center shadow gap-3 mt-8">
      <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">{title}</h3>

      {options && (
        <div className="flex justify-between items-end w-full gap-4">
          {options.map((option, index) => (
            <div key={index} className="flex-1">
              <label className="text-gray-600 mb-1 block">{option.label}</label>
              <Select
                isMulti
                options={option.data}
                value={localSelectedValues[option.name] || []}
                onChange={(selectedList) => {
                  // Only update local state, don't trigger API calls
                  setLocalSelectedValues((prev) => ({
                    ...prev,
                    [option.name]: selectedList
                  }));
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => `${option.id}-${option.workType_id ?? ''}`}
                placeholder=""
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    minHeight: '38px',
                    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? '#bfdbfe' : 'white',
                    color: '#374151',
                    '&:hover': {
                      backgroundColor: state.isSelected ? '#bfdbfe' : '#f3f4f6'
                    }
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: '#bfdbfe',
                    color: '#374151'
                  }),
                  multiValueLabel: (provided) => ({
                    ...provided,
                    color: '#374151'
                  }),
                  multiValueRemove: (provided) => ({
                    ...provided,
                    color: '#ef4444',
                    '&:hover': {
                      backgroundColor: '#fecaca',
                      color: '#dc2626'
                    }
                  })
                }}
              />
            </div>
          ))}
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                options.forEach((option) => {
                  const currentValue = localSelectedValues[option.name];
                  if (currentValue) {
                    onSelectionChange(option.name, currentValue);
                  }
                });
              }}
              className="px-4 py-[0.36rem] bg-transparent text-[#2a565e] border border-[#2a565e] rounded-md hover:bg-[#2a565e] hover:text-white transition-colors duration-200 font-medium">
              ✓
            </button>
          </div>
        </div>
      )}

      <div className="bg-[#d5d6d6] h-[2px] w-[100%] mb-4" />

      <ResponsiveContainer width="100%" height={Math.max(350, data.length * 80)}>
        {isSubmitting ? (
          <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
            Loading {isProgressChart ? 'progress' : 'money utilization'} data...
          </div>
        ) : data.length > 0 ? (
          <BarChart
            data={data}
            layout={isMoneyChart ? 'horizontal' : 'vertical'}
            margin={{ top: 0, right: 10, left: 10, bottom: calculateBottomMargin() }}
            barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" />
            {isMoneyChart ? (
              <>
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} />
                <YAxis
                  type="number"
                  domain={getDynamicYAxisConfig(data).domain}
                  allowDataOverflow={true}
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  ticks={getDynamicYAxisConfig(data).ticks}
                  tickFormatter={(value) => {
                    if (value < 1000) {
                      return value.toString();
                    } else if (value < 10000) {
                      return value.toString();
                    } else {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                  }}
                  label={{ value: xAxisLabel, position: 'insideLeft', offset: -5, angle: -90 }}
                />
              </>
            ) : (
              <>
                <XAxis
                  type="number"
                  domain={getDynamicXAxisConfig(data).domain}
                  tick={{ fontSize: 12 }}
                  ticks={getDynamicXAxisConfig(data).ticks}
                  allowDataOverflow={true}
                  label={{ value: xAxisLabel, position: 'insideBottom', offset: -5 }}
                />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 14 }} />
              </>
            )}
            {showLegend && (
              <Legend verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: '20px' }} />
            )}
            {isProgressChart ? (
              <>
                <Bar dataKey="planned" fill="#6B7280" name="Planned Duration" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="planned" content={PlannedDurationLabel} />
                </Bar>
                <Bar dataKey="actual" fill="#0EA5E9" name="Actual Duration" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="actual" content={ActualDurationLabel} />
                </Bar>
              </>
            ) : (
              <Bar dataKey="total" fill="#0EA5E9" name="Total" radius={[0, 4, 4, 0]}>
                <LabelList
                  dataKey="total"
                  content={({ x, y, width, height, value }) => {
                    const yAxisConfig = getDynamicYAxisConfig(data);
                    const chartMax = yAxisConfig.domain[1];

                    // Format label
                    const displayValue =
                      value === 0
                        ? '₹0'
                        : value < 1000
                          ? new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }).format(value)
                          : `₹${(value / 1000).toFixed(1)}K`;

                    // --- CASE: VALUE IS ZERO ---
                    if (value === 0) {
                      return (
                        <text
                          x={x + width / 2}
                          y={y - 5}
                          textAnchor="middle"
                          style={{
                            fontSize: 12,
                            fill: '#0EA5E9',
                            fontWeight: 500
                          }}>
                          {displayValue}
                        </text>
                      );
                    }

                    // --- CASE: VALUE IS > 0 (NORMAL OR CAPPED BAR) ---
                    // Show value above the bar
                    return (
                      <text
                        x={x + width / 2}
                        y={y - 5}
                        textAnchor="middle"
                        style={{
                          fontSize: 12,
                          fill: '#0EA5E9',
                          fontWeight: 500
                        }}>
                        {displayValue}
                      </text>
                    );
                  }}
                />
              </Bar>
            )}
            <Legend />
          </BarChart>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
            {selectedValues && Object.values(selectedValues).some((val) => val?.length > 0)
              ? `No ${isProgressChart ? 'progress' : 'money utilization'} data available for selected items`
              : `Please select items to view ${isProgressChart ? 'progress' : 'money utilization'} data`}
          </div>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default ProgressChart;
